import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["filtersPopover", "watchThisFirst"]

  static values = {
    contentUrl: String,
    contentCategory: { type: String, default: "all" },
    contentSort: { type: String, default: "popular" },
    contentPreviewRole: { type: String, default: "" },
    selectedFilters: { type: Array, default: [] },
    watchThisFirstPreviewUrl: String
  }

  connect() {
    this.syncFormatFilters()
    this.syncFiltersTrigger()
    this.applyWatchThisFirstBackground()
  }

  applyWatchThisFirstBackground() {
    if (!this.hasWatchThisFirstPreviewUrlValue || !this.watchThisFirstPreviewUrlValue) return
    if (!this.hasWatchThisFirstTarget) return

    this.watchThisFirstTarget.style.backgroundImage = `url("${this.watchThisFirstPreviewUrlValue}")`
  }

  selectContentTab(event) {
    event.preventDefault()

    this.contentCategoryValue = event.currentTarget.dataset.contentTabValue
    this.syncTabSelection()
    this.syncFormatFilters()
    this.loadContent()
  }

  selectContentSort(event) {
    event.preventDefault()

    this.contentSortValue = event.currentTarget.dataset.sortValue
    this.syncSortSelection()
    this.loadContent()
  }

  selectContentPreview(event) {
    event.preventDefault()

    this.contentPreviewRoleValue = event.currentTarget.dataset.previewRoleValue || ""
    this.syncPreviewSelection()
    this.loadContent()
  }

  applyContentFilters(event) {
    this.selectedFiltersValue = event.detail.values
    this.syncFiltersTrigger()
    this.loadContent()
  }

  // A format belongs to a file and a learning path has none, so the Format group leaves the
  // Filters popover under the Learning Paths pill.
  //
  // This is the ONLY thing that hides it: the markup always renders the group, because only
  // the full page renders this popover — a pill switch reloads My Content alone — so a group
  // omitted server-side could never come back for a reader who landed on the pill. Running on
  // connect covers that landing; the popover is closed on load, so nothing flashes.
  //
  // Any ticked format is dropped rather than merely hidden: left in the selection it would
  // empty the list, and left in the count the Filters badge would tally a filter doing nothing.
  syncFormatFilters() {
    const section = this.element.querySelector('.filter-popover-section[data-group="group_format"]')
    if (!section) return

    const hide = this.contentCategoryValue === "learning_paths"
    section.hidden = hide
    if (!hide) return

    // The section's own "select all" checkbox carries value="1", so the filter values come
    // from the option inputs alone while every box in the section gets cleared.
    const options = [...section.querySelectorAll('input[data-filter-popover-target="input"]')]
    const formatValues = options.map((option) => option.value)
    section.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => { checkbox.checked = false })

    this.selectedFiltersValue = this.selectedFiltersValue.filter((filter) => !formatValues.includes(filter))
    this.syncFiltersTrigger()
  }

  syncTabSelection() {
    this.element.querySelectorAll(".segmented-control .app-btn").forEach((tab) => {
      const isSelected = tab.dataset.contentTabValue === this.contentCategoryValue
      tab.classList.toggle("segmented-control-option-selected", isSelected)
    })
  }

  syncPreviewSelection() {
    this.element.querySelectorAll(".content-preview-as-option").forEach((option) => {
      const isSelected = (option.dataset.previewRoleValue || "") === this.contentPreviewRoleValue
      option.classList.toggle("is-selected", isSelected)
      option.setAttribute("aria-pressed", isSelected ? "true" : "false")
    })
  }

  syncSortSelection() {
    const checkPath = "M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"

    this.element.querySelectorAll(".filter-popover-option").forEach((option) => {
      if (!option.dataset.sortValue) return

      const isSelected = option.dataset.sortValue === this.contentSortValue
      option.classList.toggle("is-selected", isSelected)

      let check = option.querySelector(".filter-popover-option-check")
      if (isSelected && !check) {
        check = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        check.classList.add("filter-popover-option-check")
        check.setAttribute("width", "14")
        check.setAttribute("height", "14")
        check.setAttribute("viewBox", "0 0 256 256")
        check.setAttribute("fill", "currentColor")

        const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
        path.setAttribute("d", checkPath)
        check.appendChild(path)
        option.appendChild(check)
      } else if (!isSelected && check) {
        check.remove()
      }
    })
  }

  syncFiltersTrigger() {
    if (!this.hasFiltersPopoverTarget) return

    const trigger = this.filtersPopoverTarget.querySelector(".filter-popover-trigger")
    if (!trigger) return

    const active = this.selectedFiltersValue.length > 0
    trigger.classList.toggle("purple-soft-btn", active)
    trigger.classList.toggle("purple-outline-btn", !active)

    let badge = trigger.querySelector(".filter-count-badge")
    if (active) {
      if (!badge) {
        badge = document.createElement("span")
        badge.className = "filter-count-badge"
        trigger.appendChild(badge)
      }
      badge.textContent = this.selectedFiltersValue.length
    } else if (badge) {
      badge.remove()
    }
  }

  async loadContent() {
    if (!this.hasContentUrlValue) return

    const url = new URL(this.contentUrlValue, window.location.origin)
    url.searchParams.set("category", this.contentCategoryValue)
    url.searchParams.set("sort", this.contentSortValue)
    if (this.contentPreviewRoleValue) {
      url.searchParams.set("preview_role", this.contentPreviewRoleValue)
    } else {
      url.searchParams.delete("preview_role")
    }
    url.searchParams.delete("filters[]")
    this.selectedFiltersValue.forEach((filter) => url.searchParams.append("filters[]", filter))

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      })

      if (!response.ok) return

      Turbo.renderStreamMessage(await response.text())
    } catch (error) {
      console.error("Failed to load hub content:", error)
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
